import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const linkClass = 'text-xs font-medium text-slate-500 transition hover:text-blue-600';

export default function NewFooter() {
  const { t, i18n } = useTranslation();
  const currentLanguage = (i18n.resolvedLanguage || i18n.language || '').toLowerCase();
  const isRtl = currentLanguage === 'ar' || currentLanguage.startsWith('ar-');
  const associationsLabel = isRtl
    ? 'الجمعيات والمنظمات'
    : currentLanguage.startsWith('en')
      ? 'Associations & organizations'
      : 'Associations & organisations';

  return (
    <footer dir={isRtl ? 'rtl' : 'ltr'} className="border-t border-slate-100 bg-white">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-[1.35fr_.75fr_.75fr_.8fr_1.15fr]">
          <div>
            <Link to="/explore" className="inline-flex items-center gap-3">
              <picture>
                <source srcSet="/logo.webp" type="image/webp" />
                <img src="/logo.png" alt="Bizerte Connect" width="56" height="56" className="h-14 w-14 object-contain" />
              </picture>
              <span className="brand-wordmark text-xl font-black leading-tight text-[#0f4fa8]">
                Bizerte <span className="block text-blue-600">Connect</span>
              </span>
            </Link>
            <p className="mt-5 max-w-[250px] text-xs leading-6 text-slate-500">{t('footer.tagline')}</p>
          </div>

          <div>
            <h2 className="mb-5 text-sm font-black text-[#10234b]">{t('footer_redesign.navigation')}</h2>
            <ul className="space-y-3">
              <li><Link to="/explore" className={linkClass}>{t('new_nav.home')}</Link></li>
              <li><Link to="/prestataires" className={linkClass}>{t('new_nav.services')}</Link></li>
              <li><Link to="/commerces" className={linkClass}>{t('new_nav.shops_places', { defaultValue: 'Lieux utiles' })}</Link></li>
              <li><Link to="/associations" className={linkClass}>{associationsLabel}</Link></li>
              <li><Link to="/evenements" className={linkClass}>{t('footer_redesign.events')}</Link></li>
              <li><Link to="/a-propos" className={linkClass}>{t('new_nav.about')}</Link></li>
            </ul>
          </div>

          <div>
            <h2 className="mb-5 text-sm font-black text-[#10234b]">{t('new_nav.services')}</h2>
            <ul className="space-y-3">
              <li><Link to="/bus-schedules" className={linkClass}>{t('new_nav.bus')}</Link></li>
              <li><Link to="/pharmacies" className={linkClass}>{t('new_nav.pharmacies')}</Link></li>
              <li><Link to="/meteo" className={linkClass}>{t('new_nav.weather')}</Link></li>
              <li><Link to="/contact" className={linkClass}>{t('footer.contact_us')}</Link></li>
            </ul>
          </div>

          <div>
            <h2 className="mb-5 text-sm font-black text-[#10234b]">{t('footer_redesign.legal')}</h2>
            <ul className="space-y-3">
              <li><Link to="/politique-de-confidentialite" className={linkClass}>{t('footer.privacy')}</Link></li>
              <li><Link to="/politique-de-confidentialite#cookies" className={linkClass}>{t('footer_redesign.cookies')}</Link></li>
              <li><Link to="/suppression-compte" className={linkClass}>{t('footer_redesign.account_deletion')}</Link></li>
            </ul>
          </div>

          <div>
            <h2 className="text-sm font-black text-[#10234b]">{t('footer_redesign.follow')}</h2>
            <div className="mt-4 flex gap-3">
              <a href="https://www.facebook.com/bizerteconnect/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-[#1877f2] shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
                <svg className="h-6 w-6" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="11" fill="#1877F2" /><path d="M15.8 15.18 16.29 12h-3.05V9.94c0-.87.43-1.72 1.79-1.72h1.39V5.51s-1.26-.22-2.47-.22c-2.52 0-4.17 1.53-4.17 4.3V12H7v3.18h2.78V23a11.2 11.2 0 0 0 3.46 0v-7.82h2.56Z" fill="#fff" /></svg>
              </a>
              <a href="https://www.instagram.com/bizerteconnect/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
                <svg className="h-6 w-6" viewBox="0 0 24 24" aria-hidden="true">
                  <defs><radialGradient id="instagramBrand" cx="30%" cy="105%" r="120%"><stop offset="0%" stopColor="#FFD600" /><stop offset="35%" stopColor="#FF7A00" /><stop offset="62%" stopColor="#FF0169" /><stop offset="100%" stopColor="#D300C5" /></radialGradient></defs>
                  <rect x="1" y="1" width="22" height="22" rx="6.5" fill="url(#instagramBrand)" />
                  <rect x="5.2" y="5.2" width="13.6" height="13.6" rx="4.1" fill="none" stroke="#fff" strokeWidth="1.8" />
                  <circle cx="12" cy="12" r="3.25" fill="none" stroke="#fff" strokeWidth="1.8" />
                  <circle cx="17.1" cy="6.9" r="1.05" fill="#fff" />
                </svg>
              </a>
              <a href="https://www.tiktok.com/@bizerteconnect" target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-950 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
                <svg className="h-6 w-6" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M15.2 4.2c.7 1.4 1.8 2.3 3.4 2.5v3a8.2 8.2 0 0 1-3.4-1v5.7a5.5 5.5 0 1 1-4.8-5.5v3.1a2.5 2.5 0 1 0 1.8 2.4V2.5h3v1.7Z" fill="#25F4EE" transform="translate(-.65 .45)" />
                  <path d="M15.2 4.2c.7 1.4 1.8 2.3 3.4 2.5v3a8.2 8.2 0 0 1-3.4-1v5.7a5.5 5.5 0 1 1-4.8-5.5v3.1a2.5 2.5 0 1 0 1.8 2.4V2.5h3v1.7Z" fill="#FE2C55" transform="translate(.65 -.35)" />
                  <path d="M15.2 4.2c.7 1.4 1.8 2.3 3.4 2.5v3a8.2 8.2 0 0 1-3.4-1v5.7a5.5 5.5 0 1 1-4.8-5.5v3.1a2.5 2.5 0 1 0 1.8 2.4V2.5h3v1.7Z" fill="#111" />
                </svg>
              </a>
            </div>

          </div>
        </div>

        <div className="mt-10 flex flex-col gap-3 border-t border-slate-100 pt-6 text-[11px] text-slate-400 sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} <strong className="font-bold">Bizerte Connect</strong>. {t('footer.rights')}</p>
          <p>{t('footer_redesign.location')}</p>
        </div>
      </div>
    </footer>
  );
}
